import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import Navigation from "../../Navigation";
import Footer from "../../Footer";

const MusicClassificationPage = () => {
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);

  // 初始化section refs
  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 2);
  }, []);

  // 处理滚动
  useEffect(() => {
    const handleScroll = () => {
      const currentPosition = window.pageYOffset + 300;
      let active = 0;

      sectionRefs.current.forEach((ref, index) => {
        if (ref && ref.offsetTop <= currentPosition) {
          active = index;
        }
      });

      if (active !== activeSection) {
        setActiveSection(active);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection]);

  // 翻译
  const translations = {
    backToProjects: {
      en: "Back to Projects",
      zh: "返回项目列表",
      ja: "プロジェクトに戻る",
    },
    title: {
      en: "Music Genre Classification",
      zh: "音乐流派分类",
      ja: "音楽ジャンル分類",
    },
    tagline: {
      en: "Machine learning models for predicting music genres using Spotify audio features",
      zh: "利用Spotify音频特征的机器学习音乐流派预测模型",
      ja: "Spotifyのオーディオ特徴を使用した音楽ジャンル予測機械学習モデル",
    },
    overview: {
      en: "Project Overview",
      zh: "项目概述",
      ja: "プロジェクト概要",
    },
    description: {
      en: "This capstone project uses machine learning to classify music tracks into 10 different genres based on Spotify's audio features such as tempo, energy, and acousticness. The project implements dimensionality reduction techniques and various classification models to achieve high accuracy in genre prediction.",
      zh: "这个毕业项目利用机器学习根据Spotify的音频特征（如速度、能量和原声性）将音乐曲目分类为10种不同的流派。项目实现了降维技术和各种分类模型，以实现高精度的流派预测。",
      ja: "この卒業プロジェクトは、Spotifyのオーディオ特性（テンポ、エネルギー、アコースティックさなど）に基づいて音楽を10の異なるジャンルに分類する機械学習を使用します。次元削減技術とさまざまな分類モデルを実装して、ジャンル予測の高い精度を実現しています。",
    },
    methodology: {
      en: "Methodology",
      zh: "方法论",
      ja: "方法論",
    },
    methodologyText: {
      en: "We explored three main classification models: Random Forest, Support Vector Machine (SVM), and Neural Network (MLP). Principal Component Analysis (PCA) and t-SNE were employed for dimensionality reduction, with model performance evaluated using accuracy, ROC curves, and AUC scores.",
      zh: "我们探索了三种主要分类模型：随机森林、支持向量机（SVM）和神经网络（MLP）。使用主成分分析（PCA）和t-SNE进行降维，并通过准确度、ROC曲线和AUC分数评估模型性能。",
      ja: "ランダムフォレスト、サポートベクターマシン（SVM）、ニューラルネットワーク（MLP）の3つの主要分類モデルを探索しました。主成分分析（PCA）とt-SNEを次元削減に使用し、精度、ROC曲線、AUCスコアを用いてモデルのパフォーマンスを評価しました。",
    },
    results: {
      en: "Key Findings",
      zh: "主要发现",
      ja: "主な発見",
    },
    resultsText: {
      en: "The Neural Network (MLP) model emerged as the best performer with 'relu' activation and PCA 20 components, achieving 56% accuracy and AUC scores ranging from 0.84 to 0.98. This significantly outperformed both Random Forest (39% accuracy) and SVM (31% accuracy) models.",
      zh: "神经网络（MLP）模型以'relu'激活函数和PCA 20个组件表现最佳，准确率达56%，AUC分数在0.84到0.98之间。这明显优于随机森林（39%准确率）和SVM（31%准确率）模型。",
      ja: "ニューラルネットワーク（MLP）モデルは「relu」活性化関数とPCA 20コンポーネントで最高のパフォーマンスを示し、56％の精度と0.84から0.98の範囲のAUCスコアを達成しました。これはランダムフォレスト（39％精度）とSVM（31％精度）の両モデルを大幅に上回りました。",
    },
    courseInfo: {
      en: "Course Information",
      zh: "课程信息",
      ja: "コース情報",
    },
    course: {
      en: "Course:",
      zh: "课程:",
      ja: "コース:",
    },
    instructor: {
      en: "Instructor:",
      zh: "教师:",
      ja: "教授:",
    },
    pdfPreview: {
      en: "Project Report Preview",
      zh: "项目报告预览",
      ja: "プロジェクトレポートプレビュー",
    },
    viewFullReport: {
      en: "Click to view full report",
      zh: "点击查看完整报告",
      ja: "クリックして完全なレポートを表示",
    },
    closeFullView: {
      en: "Close full view",
      zh: "关闭全屏查看",
      ja: "全画面表示を閉じる",
    },
  };

  // 技术栈数据
  const techStack = [
    { name: "Python", category: "language", color: "blue" },
    { name: "Scikit-learn", category: "library", color: "green" },
    { name: "Neural Networks", category: "concept", color: "purple" },
    { name: "PCA", category: "concept", color: "purple" },
    { name: "t-SNE", category: "concept", color: "purple" },
    { name: "Data Visualization", category: "feature", color: "teal" },
  ];

  // 按类别获取技术标签的颜色
  const getTagColor = (category) => {
    switch (category) {
      case "language":
        return "bg-blue-100 text-blue-800";
      case "library":
        return "bg-green-100 text-green-800";
      case "concept":
        return "bg-purple-100 text-purple-800";
      case "feature":
        return "bg-teal-100 text-teal-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50">
      <Navigation activeSection="projects" />
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-6 flex justify-between items-center">
            <a
              href="/projects"
              className="text-primary-500 hover:text-primary-600 transition-colors flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {translations.backToProjects[language] ||
                translations.backToProjects.en}
            </a>
          </div>

          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-light mb-4 text-neutral-800">
              {translations.title[language] || translations.title.en}
            </h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              {translations.tagline[language] || translations.tagline.en}
            </p>

            {/* 技术栈标签 */}
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {techStack.map((tech, index) => (
                <span
                  key={index}
                  className={`${getTagColor(
                    tech.category
                  )} px-3 py-1 rounded-full text-sm font-medium`}
                >
                  {tech.name}
                </span>
              ))}
            </div>
          </header>

          {/* 项目概述 */}
          <section
            ref={(el) => (sectionRefs.current[0] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.overview[language] || translations.overview.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.description[language] ||
                translations.description.en}
            </p>

            {/* 项目展示图 */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
              <div className="p-2">
                <img
                  src="/assets/projects/musicClassification/roc_curve.png"
                  alt="ROC Curve for Music Genre Classification"
                  className="w-full rounded-lg"
                />
                <div className="text-center text-sm text-neutral-500 mt-2">
                  {language === "en"
                    ? "Multi-class ROC curve showing classification performance across 10 music genres"
                    : language === "zh"
                    ? "多类ROC曲线展示10种音乐流派的分类性能"
                    : "10の音楽ジャンルにおける分類性能を示すマルチクラスROC曲線"}
                </div>
              </div>
            </div>

            {/* 课程信息 */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h3 className="text-lg font-medium mb-4 text-neutral-800">
                {translations.courseInfo[language] ||
                  translations.courseInfo.en}
              </h3>
              <p className="text-neutral-700 mb-2">
                <span className="font-medium">
                  {translations.course[language] || translations.course.en}
                </span>{" "}
                Introduction to Machine Learning (Capstone Project)
              </p>
              <p className="text-neutral-700 mb-2">
                <span className="font-medium">
                  {translations.instructor[language] ||
                    translations.instructor.en}
                </span>{" "}
                Prof. Pascal Wallisch
              </p>
            </div>
          </section>

          {/* 方法论 */}
          <section
            ref={(el) => (sectionRefs.current[1] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.methodology[language] ||
                translations.methodology.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.methodologyText[language] ||
                translations.methodologyText.en}
            </p>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/2 p-6 flex flex-col justify-center">
                  <h3 className="text-xl font-medium mb-3 text-neutral-800">
                    {translations.results[language] || translations.results.en}
                  </h3>
                  <p className="text-neutral-600">
                    {translations.resultsText[language] ||
                      translations.resultsText.en}
                  </p>
                </div>
                <div className="md:w-1/2 bg-gray-50 p-4 flex items-center justify-center">
                  <img
                    src="/assets/projects/musicClassification/tsne_clusters.png"
                    alt="t-SNE visualization of music genres"
                    className="max-w-full rounded-lg"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MusicClassificationPage;
